import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createBlock as _createBlock, unref as _unref, withModifiers as _withModifiers, renderList as _renderList } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "text-body-2"
};
const _hoisted_2 = {
    key: 0,
    class: "text-body-2 mb-3"
};
import { computed, nextTick, reactive, ref, watch } from "vue";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";
import TextDateInput from "@/components/TextDateInput.vue";
import { openStepOf, stepRoute } from "@/models/intakeSteps";
import { shortDate } from "@/models/missionControl";
import { BENEFIT_TYPE_ITEMS, RequestKey, searchIcd10Codes, searchPayers, startNotes, manualEntryStartErrors, manualEntryStartService, } from "@/services/manualEntryStartService";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { tryParseApiErrors } from "@/utils";
import { localDateToIso } from "@/utils/dateUtils";
import { valueInItemsAutocompleteRule } from "@/utils/form";
import { isNPIValid } from "@/utils/validators";
const MANUAL_ENTRY = "manual_entry";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ManualEntryStartDialog',
    props: {
        modelValue: { type: Boolean },
        step: {},
        patient: { default: null },
        coverages: { default: () => [] },
        orderKey: { default: null }
    },
    emits: ["update:modelValue", "started"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const TITLES = {
            bv: "Start benefit verification",
            mn: "Start medical necessity",
            mn_on_pa: "Start medical necessity on this PA",
        };
        const REFERRALS = [
            { title: "Initial referral", value: "initial" },
            { title: "Continuation of therapy", value: "continuation" },
        ];
        const GENDERS = ["Male", "Female"];
        const OVERRIDES = { not_found: "This patient is no longer on the list. Reload the page." };
        const PA_OVERRIDES = {
            ...OVERRIDES,
            locked: "This PA's medical necessity order is on a case assigned to someone else.",
        };
        const requirementsStore = useRequirementsStore();
        const formRef = ref(null);
        const errorRef = ref(null);
        const requestKey = new RequestKey();
        const form = reactive(blankForm());
        const submitting = ref(false);
        const submitted = ref(false);
        const errors = ref([]);
        const result = ref(null);
        const genderOf = (value) => GENDERS.find((gender) => value && gender[0] === value[0].toUpperCase());
        const benefitTitle = (value) => BENEFIT_TYPE_ITEMS.find((item) => item.value === value)?.title;
        const required = (value) => Boolean(value) || "This field is required";
        const drugRule = (value) => Boolean(value?.trim()) || "Enter the drug. The order cannot move on without it.";
        const submittedRequired = (value) => !submitted.value || required(value);
        const npiRule = (value) => !value || isNPIValid(value, true) || "Enter a valid 10-digit NPI";
        const icd10Rule = (value, remote) => valueInItemsAutocompleteRule(value, "Enter a valid ICD-10 code", remote);
        const label = (text) => (props.step === "mn" ? text : `${text} (optional)`);
        const onSearchPayer = (value) => searchPayers(requirementsStore.clientConfig?.id, value);
        function blankForm() {
            return {
                firstName: "",
                lastName: "",
                dob: null,
                mrn: "",
                coverage: MANUAL_ENTRY,
                payerNameId: "",
                benefitType: null,
                memberId: "",
                drugName: "",
                diagnosis: "",
                providerNpi: "",
                referralStatus: null,
                gender: null,
            };
        }
        const activeCoverages = computed(() => props.coverages.filter((coverage) => coverage.isActive));
        const coverageItems = computed(() => [
            ...activeCoverages.value.map((coverage) => ({
                title: [coverage.memberId, coverage.insuranceProvider || "Payer not set", benefitTitle(coverage.benefitType)]
                    .filter(Boolean)
                    .join(" · "),
                value: coverage.id,
            })),
            { title: "Type another coverage", value: MANUAL_ENTRY },
        ]);
        const patientLine = computed(() => {
            const patient = props.patient;
            if (!patient)
                return "";
            const dob = shortDate(patient.dob);
            return [`${patient.firstName} ${patient.lastName}`, dob && `DOB ${dob}`, patient.mrn && `MRN ${patient.mrn}`]
                .filter(Boolean)
                .join(" · ");
        });
        const patientColumns = computed(() => (props.step === "mn" && !props.patient ? 4 : 6));
        const needed = computed(() => (props.step === "mn" ? [required] : []));
        const successText = computed(() => {
            const done = result.value;
            if (!done)
                return "";
            if (props.step === "bv")
                return "Benefit verification started.";
            if (props.step === "mn")
                return "Medical necessity started.";
            if (done.reopened)
                return "The PA's medical necessity opened again for a renewal.";
            return done.created ? "Medical necessity started on this PA." : "This PA's medical necessity order is ready.";
        });
        const notes = computed(() => result.value ? startNotes(result.value, props.step === "bv" ? "benefit verification" : "review") : []);
        const openModule = computed(() => openStepOf(result.value?.status) ?? (props.step === "bv" ? "benefits_verification" : "clinical_review"));
        const openRoute = computed(() => (result.value ? stepRoute(openModule.value, result.value.intakeOrderId) : undefined));
        const openLabel = computed(() => (openModule.value === "benefits_verification" ? "Open BV" : "Open MN"));
        watch(() => props.modelValue, (open) => {
            if (open)
                reset();
        }, { immediate: true });
        function reset() {
            Object.assign(form, blankForm(), {
                coverage: activeCoverages.value[0]?.id ?? MANUAL_ENTRY,
                gender: genderOf(props.patient?.gender) ?? null,
            });
            errors.value = [];
            result.value = null;
            submitted.value = false;
        }
        function close() {
            if (submitting.value)
                return;
            emit("update:modelValue", false);
        }
        function body() {
            const mrn = form.mrn.trim();
            const patient = props.patient
                ? { id: props.patient.id }
                : {
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    dob: localDateToIso(form.dob),
                    ...(mrn ? { mrn } : {}),
                };
            const coverage = form.coverage === MANUAL_ENTRY
                ? {
                    payerNameId: Number(form.payerNameId),
                    benefitType: form.benefitType ?? undefined,
                    memberId: form.memberId.trim(),
                }
                : { id: Number(form.coverage) };
            const order = {
                drugName: form.drugName.trim() || null,
                diagnosisIcd10Code: form.diagnosis || null,
                providerNpi: form.providerNpi.trim() || null,
                referralStatus: form.referralStatus,
                ...(props.step === "mn" ? { patientGender: form.gender } : {}),
            };
            return { patient, coverage, order };
        }
        function start() {
            if (props.step === "mn_on_pa") {
                const orderKey = props.orderKey ?? "";
                return manualEntryStartService.startMnOnPa(requestKey.for(orderKey), orderKey);
            }
            const sent = body();
            const key = requestKey.for(sent);
            return props.step === "bv" ? manualEntryStartService.startBv(key, sent) : manualEntryStartService.startMn(key, sent);
        }
        async function submit() {
            if (submitting.value)
                return;
            submitting.value = true;
            errors.value = [];
            submitted.value = true;
            try {
                if (props.step !== "mn_on_pa" && !(await formRef.value?.validate())?.valid)
                    return;
                result.value = await start();
                requestKey.reset();
                emit("started", result.value);
            }
            catch (error) {
                const failed = error;
                if (failed?.response?.data?.error_code === "request_key_reused")
                    requestKey.reset();
                errors.value = manualEntryStartErrors(failed, props.step === "mn_on_pa" ? PA_OVERRIDES : OVERRIDES);
                if (!errors.value.length)
                    errors.value = tryParseApiErrors(error);
                if (!errors.value.length)
                    errors.value = ["The start did not go through. Try again."];
                await nextTick();
                errorRef.value?.$el.scrollIntoView({ block: "nearest" });
            }
            finally {
                submitting.value = false;
            }
        }
        return (_ctx, _cache) => {
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_row = _resolveComponent("v-row");
            const _component_v_form = _resolveComponent("v-form");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_spacer = _resolveComponent("v-spacer");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_card_actions = _resolveComponent("v-card-actions");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": __props.modelValue,
                "max-width": "640",
                scrollable: "",
                persistent: submitting.value,
                "onUpdate:modelValue": close
            }, {
                default: _withCtx(() => [
                    _createVNode(_component_v_card, { "data-testid": "manual-entry-start-dialog" }, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "text-subtitle-1" }, {
                                default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(TITLES[__props.step]), 1)
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, null, {
                                default: _withCtx(() => [
                                    (!result.value)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            (__props.step === 'mn_on_pa')
                                                ? (_openBlock(), _createElementBlock("p", _hoisted_1, " Medical necessity starts on prior authorization " + _toDisplayString(__props.orderKey) + " with the PA's patient, coverage and order details. If the PA already has its MN order, you go to that order. If that MN decided the PA, it opens again for a renewal. ", 1))
                                                : (_openBlock(), _createBlock(_component_v_form, {
                                                    key: __props.step,
                                                    ref_key: "formRef",
                                                    ref: formRef,
                                                    onSubmit: _withModifiers(submit, ["prevent"])
                                                }, {
                                                    default: _withCtx(() => [
                                                        _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-subtitle-2 mb-2" }, "Patient", -1)),
                                                        (__props.patient)
                                                            ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(patientLine.value), 1))
                                                            : _createCommentVNode("", true),
                                                        _createVNode(_component_v_row, { dense: "" }, {
                                                            default: _withCtx(() => [
                                                                (!__props.patient)
                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                        _createVNode(_component_v_col, {
                                                                            cols: "12",
                                                                            sm: "6"
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(_component_v_text_field, {
                                                                                    modelValue: form.firstName,
                                                                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((form.firstName) = $event)),
                                                                                    label: "First name",
                                                                                    rules: [required]
                                                                                }, null, 8, ["modelValue", "rules"])
                                                                            ]),
                                                                            _: 1
                                                                        }),
                                                                        _createVNode(_component_v_col, {
                                                                            cols: "12",
                                                                            sm: "6"
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(_component_v_text_field, {
                                                                                    modelValue: form.lastName,
                                                                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.lastName) = $event)),
                                                                                    label: "Last name",
                                                                                    rules: [required]
                                                                                }, null, 8, ["modelValue", "rules"])
                                                                            ]),
                                                                            _: 1
                                                                        }),
                                                                        _createVNode(_component_v_col, {
                                                                            cols: "12",
                                                                            sm: patientColumns.value
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(TextDateInput, {
                                                                                    modelValue: form.dob,
                                                                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((form.dob) = $event)),
                                                                                    label: "Date of birth",
                                                                                    rules: [required]
                                                                                }, null, 8, ["modelValue", "rules"])
                                                                            ]),
                                                                            _: 1
                                                                        }, 8, ["sm"]),
                                                                        _createVNode(_component_v_col, {
                                                                            cols: "12",
                                                                            sm: patientColumns.value
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(_component_v_text_field, {
                                                                                    modelValue: form.mrn,
                                                                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((form.mrn) = $event)),
                                                                                    label: "MRN (optional)"
                                                                                }, null, 8, ["modelValue"])
                                                                            ]),
                                                                            _: 1
                                                                        }, 8, ["sm"])
                                                                    ], 64))
                                                                    : _createCommentVNode("", true),
                                                                (__props.step === 'mn')
                                                                    ? (_openBlock(), _createBlock(_component_v_col, {
                                                                        key: 1,
                                                                        cols: "12",
                                                                        sm: patientColumns.value
                                                                    }, {
                                                                        default: _withCtx(() => [
                                                                            _createVNode(_component_v_select, {
                                                                                modelValue: form.gender,
                                                                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((form.gender) = $event)),
                                                                                items: GENDERS,
                                                                                label: "Patient gender",
                                                                                rules: [required]
                                                                            }, null, 8, ["modelValue", "rules"])
                                                                        ]),
                                                                        _: 1
                                                                    }, 8, ["sm"]))
                                                                    : _createCommentVNode("", true)
                                                            ]),
                                                            _: 1
                                                        }),
                                                        _cache[14] || (_cache[14] = _createElementVNode("div", { class: "text-subtitle-2 mt-4 mb-2" }, "Coverage", -1)),
                                                        _createVNode(_component_v_row, { dense: "" }, {
                                                            default: _withCtx(() => [
                                                                (coverageItems.value.length > 1)
                                                                    ? (_openBlock(), _createBlock(_component_v_col, {
                                                                        key: 0,
                                                                        cols: "12"
                                                                    }, {
                                                                        default: _withCtx(() => [
                                                                            _createVNode(_component_v_select, {
                                                                                modelValue: form.coverage,
                                                                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((form.coverage) = $event)),
                                                                                items: coverageItems.value,
                                                                                label: "Coverage"
                                                                            }, null, 8, ["modelValue", "items"])
                                                                        ]),
                                                                        _: 1
                                                                    }))
                                                                    : _createCommentVNode("", true),
                                                                (form.coverage === MANUAL_ENTRY)
                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                        _createVNode(_component_v_col, { cols: "12" }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(RemoteAutocomplete, {
                                                                                    modelValue: form.payerNameId,
                                                                                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((form.payerNameId) = $event)),
                                                                                    "remote-search": onSearchPayer,
                                                                                    "allow-free-text": false,
                                                                                    label: "Payer",
                                                                                    rules: [submittedRequired]
                                                                                }, null, 8, ["modelValue", "rules"])
                                                                            ]),
                                                                            _: 1
                                                                        }),
                                                                        _createVNode(_component_v_col, {
                                                                            cols: "12",
                                                                            sm: "6"
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(_component_v_select, {
                                                                                    modelValue: form.benefitType,
                                                                                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((form.benefitType) = $event)),
                                                                                    items: _unref(BENEFIT_TYPE_ITEMS),
                                                                                    label: "Benefit type",
                                                                                    rules: [required]
                                                                                }, null, 8, ["modelValue", "items", "rules"])
                                                                            ]),
                                                                            _: 1
                                                                        }),
                                                                        _createVNode(_component_v_col, {
                                                                            cols: "12",
                                                                            sm: "6"
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(_component_v_text_field, {
                                                                                    modelValue: form.memberId,
                                                                                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((form.memberId) = $event)),
                                                                                    label: "Member ID (optional)"
                                                                                }, null, 8, ["modelValue"])
                                                                            ]),
                                                                            _: 1
                                                                        })
                                                                    ], 64))
                                                                    : _createCommentVNode("", true)
                                                            ]),
                                                            _: 1
                                                        }),
                                                        _cache[15] || (_cache[15] = _createElementVNode("div", { class: "text-subtitle-2 mt-4 mb-2" }, "Order", -1)),
                                                        _createVNode(_component_v_row, { dense: "" }, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_v_col, {
                                                                    cols: "12",
                                                                    sm: "6"
                                                                }, {
                                                                    default: _withCtx(() => [
                                                                        _createVNode(_component_v_text_field, {
                                                                            modelValue: form.drugName,
                                                                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((form.drugName) = $event)),
                                                                            label: "Drug name",
                                                                            rules: [drugRule]
                                                                        }, null, 8, ["modelValue", "rules"])
                                                                    ]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_col, {
                                                                    cols: "12",
                                                                    sm: "6"
                                                                }, {
                                                                    default: _withCtx(() => [
                                                                        _createVNode(RemoteAutocomplete, {
                                                                            modelValue: form.diagnosis,
                                                                            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => ((form.diagnosis) = $event)),
                                                                            "remote-search": _unref(searchIcd10Codes),
                                                                            label: label('Diagnosis (ICD-10)'),
                                                                            rules: __props.step === 'mn' ? [submittedRequired, icd10Rule] : [icd10Rule]
                                                                        }, null, 8, ["modelValue", "remote-search", "label", "rules"])
                                                                    ]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_col, {
                                                                    cols: "12",
                                                                    sm: "6"
                                                                }, {
                                                                    default: _withCtx(() => [
                                                                        _createVNode(_component_v_text_field, {
                                                                            modelValue: form.providerNpi,
                                                                            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((form.providerNpi) = $event)),
                                                                            label: label('Prescriber NPI'),
                                                                            rules: [...needed.value, npiRule]
                                                                        }, null, 8, ["modelValue", "label", "rules"])
                                                                    ]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_col, {
                                                                    cols: "12",
                                                                    sm: "6"
                                                                }, {
                                                                    default: _withCtx(() => [
                                                                        _createVNode(_component_v_select, {
                                                                            modelValue: form.referralStatus,
                                                                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event) => ((form.referralStatus) = $event)),
                                                                            items: REFERRALS,
                                                                            label: "Referral (optional)",
                                                                            clearable: ""
                                                                        }, null, 8, ["modelValue"])
                                                                    ]),
                                                                    _: 1
                                                                })
                                                            ]),
                                                            _: 1
                                                        })
                                                    ]),
                                                    _: 1
                                                })),
                                            (errors.value.length)
                                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                                    key: 2,
                                                    ref_key: "errorRef",
                                                    ref: errorRef,
                                                    type: "error",
                                                    variant: "tonal",
                                                    density: "compact",
                                                    class: "mt-3"
                                                }, {
                                                    default: _withCtx(() => [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (error) => {
                                                            return (_openBlock(), _createElementBlock("div", { key: error }, _toDisplayString(error), 1));
                                                        }), 128))
                                                    ]),
                                                    _: 1
                                                }, 512))
                                                : _createCommentVNode("", true)
                                        ], 64))
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            _createVNode(_component_v_alert, {
                                                type: "success",
                                                variant: "tonal",
                                                density: "compact",
                                                text: successText.value
                                            }, null, 8, ["text"]),
                                            (notes.value.length)
                                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                                    key: 0,
                                                    type: "warning",
                                                    variant: "tonal",
                                                    density: "compact",
                                                    class: "mt-3"
                                                }, {
                                                    default: _withCtx(() => [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notes.value, (note) => {
                                                            return (_openBlock(), _createElementBlock("div", { key: note }, _toDisplayString(note), 1));
                                                        }), 128))
                                                    ]),
                                                    _: 1
                                                }))
                                                : _createCommentVNode("", true)
                                        ], 64))
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_actions, { class: "flex-wrap" }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_spacer),
                                    _createVNode(_component_v_btn, {
                                        variant: "text",
                                        disabled: submitting.value,
                                        onClick: close
                                    }, {
                                        default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(result.value ? "Close" : "Cancel"), 1)
                                        ]),
                                        _: 1
                                    }, 8, ["disabled"]),
                                    (result.value)
                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                            key: 0,
                                            color: "primary",
                                            variant: "flat",
                                            to: openRoute.value
                                        }, {
                                            default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(openLabel.value), 1)
                                            ]),
                                            _: 1
                                        }, 8, ["to"]))
                                        : (_openBlock(), _createBlock(_component_v_btn, {
                                            key: 1,
                                            color: "primary",
                                            variant: "flat",
                                            loading: submitting.value,
                                            onClick: submit
                                        }, {
                                            default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                                    _createTextVNode("Start", -1)
                                                ]))]),
                                            _: 1
                                        }, 8, ["loading"]))
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    })
                ]),
                _: 1
            }, 8, ["model-value", "persistent"]));
        };
    }
});
